import { useMemo } from 'react';
import { useHistoricalData } from './useHistoricalData';
import { type UnifiedModel } from '../utils/unifiedModels';
import { getCatalogUnifiedModels } from '../services/catalog';

const MAJOR_RELEASE_KEYWORDS =
  /\b(gpt|claude|gemini|llama|nova|mistral|deepseek|qwen|command|jamba|rerank-v3\.5)\b/i;
const SMALL_UPDATE_KEYWORDS =
  /\b(embed|embedding|text-embedding|multilingual-embedding|image|imagen|stable|upscale|whisper|tts|marengo|pegasus)\b/i;

const PUBLISHER_RELEASE_WEIGHT: Record<string, number> = {
  OpenAI: 18,
  Anthropic: 18,
  Google: 16,
  Meta: 14,
  Amazon: 12,
  'Mistral AI': 12,
  DeepSeek: 12,
  Qwen: 10,
  Cohere: 8,
};

function getModelTime(model: UnifiedModel): number {
  return Math.max(
    Date.parse(model.releaseDate ?? '') || 0,
    Date.parse(model.firstCreated) || 0,
    Date.parse(model.lastUpdated) || 0
  );
}

function getReleasePriority(model: UnifiedModel, latestTime: number): number {
  const label = `${model.slug} ${model.displayName}`.toLowerCase();
  const ageDays = Math.max(0, (latestTime - getModelTime(model)) / 86_400_000);
  const recencyScore = Math.max(0, 55 - ageDays * 1.8);
  const deploymentScore = Math.min(18, model.deployments.length * 1.5);
  const publisherScore = PUBLISHER_RELEASE_WEIGHT[model.provider] ?? 4;
  const majorReleaseScore = MAJOR_RELEASE_KEYWORDS.test(label) ? 24 : 0;
  const smallUpdatePenalty = SMALL_UPDATE_KEYWORDS.test(label) ? 34 : 0;
  const contextScore =
    model.capabilities.contextWindow >= 200_000 ? 8 :
    model.capabilities.contextWindow >= 100_000 ? 5 :
    0;
  const modalityScore =
    model.capabilities.inputModalities.length > 1 || model.capabilities.outputModalities.length > 1
      ? 4
      : 0;

  return recencyScore + deploymentScore + publisherScore + majorReleaseScore + contextScore + modalityScore - smallUpdatePenalty;
}

export function getFeaturedReleaseModels(models: UnifiedModel[], count = 6): UnifiedModel[] {
  const latestTime = Math.max(...models.map(getModelTime), 0);
  return [...models]
    .sort((a, b) => {
      const priorityDelta = getReleasePriority(b, latestTime) - getReleasePriority(a, latestTime);
      if (priorityDelta !== 0) return priorityDelta;
      return getModelTime(b) - getModelTime(a);
    })
    .slice(0, count);
}

export function useUnifiedModels() {
  const { currentSnapshot, isLoading, error } = useHistoricalData();

  const unifiedModels = useMemo<UnifiedModel[]>(() => {
    return getCatalogUnifiedModels(currentSnapshot);
  }, [currentSnapshot]);

  // Get featured releases, favoring major model families over routine/utility updates.
  const newestModels = useMemo<UnifiedModel[]>(() => {
    return getFeaturedReleaseModels(unifiedModels, 6);
  }, [unifiedModels]);

  // Get provider counts
  const providerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    unifiedModels.forEach(model => {
      counts[model.provider] = (counts[model.provider] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([provider, count]) => ({ provider, count }))
      .sort((a, b) => b.count - a.count);
  }, [unifiedModels]);

  return {
    unifiedModels,
    newestModels,
    providerCounts,
    isLoading,
    error,
  };
}
